import React from 'react';
import { Badge, Table } from '@parsina/framework';
import { AlarmClock, Clock, ExternalLink } from 'lucide-react';

import { useTranslation, LTR_TEXT } from '../../i18n';
import { EMPTY } from '../../lib/format';
import { DOCTORSITE_PUBLIC_URL } from '../../lib/runtimeEnv';
import {
  DENTIST_COUNT_KEY, ENUM_ANSWERS, ageTone, doctorPageSlug, doctorPageUrl,
} from '../../lib/demoRequestsView';
import { DOCTOR_PAGE_INTENT } from '../../constants/leadPipeline';
import { LeadStatusBadge, OwnerLabel, ShortRef, useVocab } from './shared';

/**
 * The queue as a table (§3.2 T1).
 *
 * Columns answer «who is waiting, for how long, and who has it»: age (the SLA
 * colour, derived — amber past one working day, red past three, only while
 * unanswered), the person, what kind of practice, how they want the demo,
 * status, owner, and the reference code the visitor was shown.
 *
 * Rows activate on click, Enter and Space (`onRowActivate`) and open the
 * drawer; the lead's uuid is the only thing that reaches the URL.
 */

const AGE_BADGE = { warn: 'warning', danger: 'destructive' };

function AgeCell({ lead, now }) {
  const { t, formatRelativeTime, formatDateTime } = useTranslation();
  const tone = ageTone(lead, now);
  const created = Number(lead.createdAtMs) || null;
  const text = created ? formatRelativeTime(created, now) : EMPTY;
  const exact = created ? formatDateTime(created) : '';
  if (!tone) {
    return <span className="whitespace-nowrap text-sm text-muted-foreground" title={exact}>{text}</span>;
  }
  // Colour AND an icon AND words: the SLA state is never carried by colour alone.
  const why = t(`demoRequests.age.${tone}`);
  return (
    <Badge
      variant={AGE_BADGE[tone]}
      className="gap-1 whitespace-nowrap"
      title={`${exact} · ${why}`}
      data-tone={tone}
      data-testid={`demo-requests-age-${lead.id}`}
    >
      <Clock size={12} aria-hidden />
      {text}
      <span className="sr-only">{` — ${why}`}</span>
    </Badge>
  );
}

/**
 * A doctor-page request's public page: a real link (new tab) built ONLY from a
 * validated slug — `subjectRef` first, then the details. The click must not
 * also activate the row, so it stops there.
 */
export function PageLink({ lead, base = DOCTORSITE_PUBLIC_URL, 'data-testid': testId }) {
  const { t } = useTranslation();
  const slug = doctorPageSlug(lead);
  const href = doctorPageUrl(lead, base);
  if (!slug || !href) return <span>{EMPTY}</span>;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex min-w-0 items-center gap-1 text-primary hover:underline"
      aria-label={t('demoRequests.pageLeads.openPage', { slug })}
      title={href}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      data-testid={testId}
    >
      <span {...LTR_TEXT} className="truncate font-mono text-xs">{slug}</span>
      <ExternalLink size={13} aria-hidden className="shrink-0" />
    </a>
  );
}

export default function DemoRequestsTable({
  rows, loading, mySub, overdue, onOpen, empty, intent, now = Date.now(),
}) {
  const { t } = useTranslation();
  const vocab = useVocab();

  const answer = (field, value) => {
    if (value == null || value === '') return null;
    const segment = field === 'dentistCount' ? DENTIST_COUNT_KEY[value] : value;
    return vocab(segment ? `${ENUM_ANSWERS[field]}.${segment}` : null, value);
  };

  const statusColumn = {
    key: 'status',
    header: t('demoRequests.columns.status'),
    width: '10rem',
    render: (r) => (
      <div className="flex items-center gap-1.5">
        <LeadStatusBadge status={r.status} data-testid={`demo-requests-status-${r.id}`} />
        {overdue?.has(r.id) ? (
          <span
            className="inline-flex text-destructive"
            title={t('demoRequests.followUp.overdueRow')}
            data-testid={`demo-requests-overdue-${r.id}`}
          >
            <AlarmClock size={15} aria-hidden />
            <span className="sr-only">{t('demoRequests.followUp.overdueRow')}</span>
          </span>
        ) : null}
      </div>
    ),
  };
  const ageColumn = {
    key: 'age',
    header: t('demoRequests.columns.age'),
    width: '9.5rem',
    render: (r) => <AgeCell lead={r} now={now} />,
  };
  const ownerColumn = {
    key: 'owner',
    header: t('demoRequests.columns.owner'),
    width: '8rem',
    render: (r) => <OwnerLabel ownerId={r.ownerId} mySub={mySub} />,
  };

  // A patient's appointment request from a doctor's public page (§D7): whom to
  // call, for which doctor, from which page, and how they want to be reached.
  const pageColumns = [
    ageColumn,
    {
      key: 'visitor',
      header: t('demoRequests.pageLeads.columns.visitor'),
      render: (r) => (
        <div className="min-w-0">
          <div dir="auto" className="truncate font-semibold text-foreground" title={r.customerName || ''}>
            {r.customerName || t('demoRequests.unnamed')}
          </div>
          {r.customerMobile ? (
            <div {...LTR_TEXT} className="truncate font-mono text-xs text-muted-foreground" data-testid={`demo-requests-mobile-${r.id}`}>
              {r.customerMobile}
            </div>
          ) : null}
        </div>
      ),
    },
    {
      key: 'doctor',
      header: t('demoRequests.pageLeads.columns.doctor'),
      render: (r) => {
        const d = r.requestDetails || {};
        return (
          <div className="min-w-0">
            <div dir="auto" className="truncate text-sm" title={d.doctorDisplayName || ''}>{d.doctorDisplayName || EMPTY}</div>
            {d.councilNumber ? (
              <div {...LTR_TEXT} className="truncate font-mono text-xs text-muted-foreground">{d.councilNumber}</div>
            ) : null}
          </div>
        );
      },
    },
    {
      key: 'page',
      header: t('demoRequests.pageLeads.columns.page'),
      width: '10rem',
      render: (r) => <PageLink lead={r} data-testid={`demo-requests-page-${r.id}`} />,
    },
    {
      key: 'preferredContact',
      header: t('demoRequests.pageLeads.columns.preferred'),
      width: '11rem',
      render: (r) => {
        const d = r.requestDetails || {};
        const parts = [
          d.preferredChannel ? vocab(`demoRequests.channel.${d.preferredChannel}`, d.preferredChannel) : null,
          answer('preferredTime', d.preferredTime),
        ].filter(Boolean);
        const text = parts.length ? parts.join(' · ') : EMPTY;
        return <div className="truncate text-sm" title={text}>{text}</div>;
      },
    },
    statusColumn,
    ownerColumn,
  ];

  const demoColumns = [
    {
      key: 'age',
      header: t('demoRequests.columns.age'),
      width: '9.5rem',
      render: (r) => <AgeCell lead={r} now={now} />,
    },
    {
      key: 'name',
      header: t('demoRequests.columns.name'),
      render: (r) => (
        <div className="min-w-0">
          {/* A name is DATA: rendered in the script it was typed in. */}
          <div dir="auto" className="truncate font-semibold text-foreground" title={r.customerName || ''}>
            {r.customerName || t('demoRequests.unnamed')}
          </div>
          {r.requestDetails?.organization ? (
            <div dir="auto" className="truncate text-xs text-muted-foreground" title={r.requestDetails.organization}>
              {r.requestDetails.organization}
            </div>
          ) : null}
        </div>
      ),
    },
    {
      key: 'practice',
      header: t('demoRequests.columns.practice'),
      render: (r) => {
        const d = r.requestDetails || {};
        const parts = [
          answer('practiceType', d.practiceType),
          answer('dentistCount', d.dentistCount),
          d.city || null,
        ].filter(Boolean);
        const text = parts.length ? parts.join(' · ') : EMPTY;
        return <div dir="auto" className="truncate text-sm" title={text}>{text}</div>;
      },
    },
    {
      key: 'preferred',
      header: t('demoRequests.columns.preferred'),
      width: '11rem',
      render: (r) => {
        const d = r.requestDetails || {};
        const parts = [answer('demoMode', d.demoMode), answer('preferredTime', d.preferredTime)].filter(Boolean);
        const text = parts.length ? parts.join(' · ') : EMPTY;
        return <div className="truncate text-sm" title={text}>{text}</div>;
      },
    },
    statusColumn,
    ownerColumn,
    {
      key: 'reference',
      header: t('demoRequests.columns.reference'),
      width: '8rem',
      render: (r) => <ShortRef value={r.requestDetails?.reference} />,
    },
  ];

  const columns = intent === DOCTOR_PAGE_INTENT ? pageColumns : demoColumns;

  return (
    <Table
      dense
      loading={loading}
      skeletonRows={8}
      ariaLabel={t('demoRequests.tableAria')}
      columns={columns}
      rows={rows}
      rowKey={(r) => r.id}
      onRowActivate={onOpen}
      rowProps={(r) => ({
        'data-testid': `demo-requests-row-${r.id}`,
        // Names the row for a screen reader by the only thing that is not PII
        // in the address bar sense — it is on screen anyway, and the row must
        // say what Enter will open.
        'aria-label': t('demoRequests.open', { name: r.customerName || t('demoRequests.unnamed') }),
      })}
      empty={empty}
      data-testid="demo-requests-table"
    />
  );
}
